import { pickAirportBanner, pickAirlineBanner, airportBannerUrl } from "../lib/banner";
import useWeatherBoard from "../hooks/useWeatherBoard";

/* Base banners that a weather-live override may replace. Keeps the swap tasteful:
   we never swap climate-specific illustrations (alpine, nordic, desert, monsoon) or
   a bespoke hero — only generic city/plains/tropical/mediterranean etc. */
const WEATHER_SWAPPABLE = new Set([
  "/images/airport-banners/city.webp",
  "/images/airport-banners/plains.webp",
  "/images/airport-banners/tropical.webp",
  "/images/airport-banners/mediterranean.webp",
  "/images/airport-banners/lake.webp",
  "/images/airport-banners/heritage.webp",
]);

/* <TopBoard> — reusable illustrated top hero for airport/airline detail pages.
   Renders one of the curated banner illustrations behind a soft gradient +
   the entity title + optional metadata badge and subtitle. Airport boards can
   swap in real time based on Open-Meteo current weather. */

export default function TopBoard({ entity, kind, title, subtitle, badge, children }) {
  const base = kind === "airline" ? pickAirlineBanner(entity || {}) : pickAirportBanner(entity || {});
  const weatherKey = useWeatherBoard(kind === "airport" ? entity : null);
  const weatherUrl = weatherKey ? airportBannerUrl(weatherKey) : null;
  const src = (kind === "airport" && weatherUrl && WEATHER_SWAPPABLE.has(base)) ? weatherUrl : base;
  return (
    <section className="relative border-b border-zinc-100" data-testid={`top-board-${kind}`} data-banner={src}>
      <div className="absolute inset-0 overflow-hidden">
        <img src={src} alt="" aria-hidden loading="eager"
          className="w-full h-full object-cover"
          onError={(e) => { e.target.style.display = "none"; }} />
        <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/55 to-transparent" />
      </div>
      <div className="relative max-w-6xl mx-auto px-5 py-10 sm:py-14">
        {badge && (
          <div className="inline-flex items-center gap-1.5 bg-white/95 border border-zinc-200 rounded-full px-3 py-1 text-[11px] font-800 uppercase tracking-wide text-zinc-800 mb-4" data-testid="top-board-badge">
            {badge}
          </div>
        )}
        <h1 className="font-heading font-900 text-3xl sm:text-4xl lg:text-[42px] text-[#0A2A5E] leading-[1.15] tracking-tight drop-shadow-[0_1px_3px_rgba(255,255,255,0.9)]" data-testid="top-board-title">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-2 text-sm sm:text-base font-600 text-zinc-800 max-w-2xl drop-shadow-[0_1px_2px_rgba(255,255,255,0.9)]" data-testid="top-board-subtitle">
            {subtitle}
          </p>
        )}
        {weatherKey && (
          <div className="absolute right-4 top-4 text-[10px] font-800 uppercase tracking-wider bg-white/80 border border-zinc-200 rounded-full px-2.5 py-0.5 text-zinc-700" data-testid="top-board-weather-tag">
            {weatherKey === "monsoon" ? "Rain now" : weatherKey === "clear" ? "Clear sky" : ""}
          </div>
        )}
        {children && <div className="mt-4">{children}</div>}
      </div>
    </section>
  );
}
